<template>
    <div class="pp-poster" noautohide @click="emit('play')">
        <img v-if="artwork" :src="artwork" alt="" class="pp-poster-backdrop" />
        <div class="pp-poster-center">
            <button
                type="button"
                class="pp-poster-play"
                :class="{ 'pp-poster-play--accent': accentColor }"
                :aria-label="t('Play')"
                @click.stop="emit('play')"
            >
                <VideoIcon name="play" :size="30" />
            </button>
            <p class="vp-heading pp-poster-title">{{ title }}</p>
        </div>
    </div>
</template>

<script setup>
import VideoIcon from "../../PlayerCore/Components/VideoIcon.vue"
import { useCoreI18n } from "../../PlayerCore/useCoreI18n.js"

defineProps({
    artwork: { type: String, default: null },
    title: { type: String, default: "" },
    // Opt-in only — this component is shared with the Portal Player (PortalPlayer.vue), which
    // has no "custom theme" concept at all and must keep its own plain white/ink button
    // regardless. The v2 player (PlayerV2.vue) passes this true so its own Player template
    // colors (see theme.js's --vp-accent) actually show up on the main play action, not just
    // the thin progress bar.
    accentColor: { type: Boolean, default: false },
})

const emit = defineEmits(["play"])
const t = useCoreI18n()
</script>

<style>
.pp-poster {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vp-ink);
    cursor: pointer;
}

.pp-poster-backdrop {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
}

.pp-poster-center {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 24px;
    text-align: center;
}

.pp-poster-play {
    width: 72px;
    height: 72px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--vp-ink);
    background: var(--vp-paper);
    cursor: pointer;
}

/* The player's one "pop" color (same --vp-accent the timeline/progress bar already uses — see
   theme.js) instead of the generic white circle above, so the main play action is actually
   colored per template too, not just the thin progress bar. See accentColor prop above for why
   this is opt-in rather than just repainting .pp-poster-play directly. */
.pp-poster-play--accent {
    color: var(--vp-paper);
    background: var(--vp-accent);
}

.pp-poster-play:focus-visible {
    outline: 2px solid var(--vp-accent);
    outline-offset: 3px;
}

.pp-poster-title {
    margin: 0;
    max-width: 560px;
    font-size: 20px;
}
</style>
